import { useState, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useHistoricalData } from '../hooks/use-historical-data'
import { useApiConfig } from '../hooks/use-api-config'
import HistoricalControls from './HistoricalControls'
import HistoricalGraphs from './HistoricalGraphs'
import DataTable from './DataTable'

function HistoricalTab({ config, availableFields, sensors, selectedSensorId, onSensorChange, groupSensorsByCustomer }) {
  const { data: historicalData, loading, fetchData } = useHistoricalData()
  const { max_time_range_hours } = useApiConfig(config?.customerId)
  const [selectedFields, setSelectedFields] = useState({
    alkalinity_ppm: true,
    ph: true,
    temperature: true,
    conductivity: true
  })
  const [aggregationInterval, setAggregationInterval] = useState('raw')
  const [timezone, setTimezone] = useState('UTC')
  const lastRangeRef = useRef(null)

  const handleLoadData = async (startTime, endTime) => {
    const startISO = new Date(startTime).toISOString()
    const endISO = new Date(endTime).toISOString()
    lastRangeRef.current = { startISO, endISO }

    try {
      await fetchData(config.customerId, config.sensorId, startISO, endISO, aggregationInterval)
    } catch (error) {
      console.error('Error loading historical data:', error)
      alert(`Error loading data: ${error.message}`)
    }
  }

  const handleAggregationChange = (interval) => {
    setAggregationInterval(interval)
    if (!lastRangeRef.current || !config?.customerId || !config?.sensorId) return
    const { startISO, endISO } = lastRangeRef.current
    fetchData(config.customerId, config.sensorId, startISO, endISO, interval)
  }

  const handleDownloadCSV = () => {
    if (!historicalData?.data) {
      alert('Please load data first before downloading CSV')
      return
    }

    const fields = Object.keys(selectedFields).filter(k => selectedFields[k] && availableFields[k])
    
    // Generate CSV from loaded data
    const measurements = historicalData.data
    const headers = ['timestamp']
    const fieldMapping = {
      alkalinity_ppm: 'total_alkalinity_ppm_rolling_avg',
      ph: 'ph',
      temperature: 'temperature_c',
      conductivity: 'conductivity_mscm'
    }

    // Add selected field headers
    fields.forEach(field => {
      if (availableFields[field]) {
        headers.push(field)
      }
    })

    // Build CSV content
    let csvContent = headers.join(',') + '\n'
    
    measurements.forEach(m => {
      const row = [m.timestamp]
      fields.forEach(field => {
        const dbField = fieldMapping[field]
        row.push(m[dbField] != null ? m[dbField] : '')
      })
      csvContent += row.join(',') + '\n'
    })

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `sensor_data_${config.sensorId}_${new Date().toISOString()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // Data is already aggregated server-side when interval !== raw; skip client re-aggregation
  const chartInterval = 'raw'

  return (
    <Box>
      <HistoricalControls
        availableFields={availableFields}
        selectedFields={selectedFields}
        onFieldChange={setSelectedFields}
        onLoadData={handleLoadData}
        onDownloadCSV={handleDownloadCSV}
        loading={loading}
        hasData={!!historicalData}
        maxTimeRangeHours={max_time_range_hours}
        sensors={sensors}
        selectedSensorId={selectedSensorId}
        onSensorChange={onSensorChange}
        groupSensorsByCustomer={groupSensorsByCustomer}
        aggregationInterval={aggregationInterval}
        onAggregationChange={handleAggregationChange}
        timezone={timezone}
        onTimezoneChange={setTimezone}
      />

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress sx={{ mr: 2 }} />
          <Typography>Loading data...</Typography>
        </Box>
      )}

      {!loading && historicalData && (
        <>
          <HistoricalGraphs
            data={historicalData.data}
            selectedFields={selectedFields}
            availableFields={availableFields}
            aggregationInterval={chartInterval}
            timezone={timezone}
          />
          <DataTable
            data={historicalData.data}
            selectedFields={selectedFields}
            availableFields={availableFields}
          />
        </>
      )}
    </Box>
  )
}

export default HistoricalTab
